/**
 * TemplateFormContext
 *
 * Purpose: React context that provides field registry, form state, and actions
 *          to all child components (Field, ExtraFields) within a TemplateForm
 * Architecture: Context + Provider pattern consumed by TemplateField, ExtraFields,
 *               and FieldGroupContext to access shared form state
 * Dependencies: React (createContext, useContext), ./types (TemplateFormContextValue)
 * Author: Development Team
 * Created: 2026-02-15
 */

import { createContext, useContext } from 'react';
import type { TemplateFormContextValue } from './types';

/**
 * Internal React context for template form state.
 *
 * Purpose: Holds the TemplateFormContextValue provided by TemplateForm
 * Logic: Initialized to null; must be populated by TemplateFormProvider
 */
const TemplateFormContext = createContext<TemplateFormContextValue | null>(null);

/**
 * useTemplateFormContext — hook to consume the template form context
 *
 * Purpose: Provides type-safe access to form state, field registry, and actions
 * Logic: Reads the TemplateFormContext; throws if called outside a TemplateForm
 * Input: None
 * Output: TemplateFormContextValue with fieldRegistry, values, errors, touched, actions
 * Exceptions: Throws Error if called outside a <TemplateForm> provider
 */
export const useTemplateFormContext = (): TemplateFormContextValue => {
  const context = useContext(TemplateFormContext);
  if (!context) {
    throw new Error('useTemplateFormContext must be used within a <TemplateForm>');
  }
  return context;
};

/** Re-export the Provider for use in TemplateForm.tsx */
export const TemplateFormProvider = TemplateFormContext.Provider;
